import {
  addBrand,
  matchBrand,
  searchBrands,
  synctoERP,
  updateBrand,
} from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { IBrand, IBrandRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';

const _modal: IDataModal<IBrand> = {
  open: false,
};

const BrandPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IBrand[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<IBrand[]>([]);
  const [modal, setModal] = useState<IDataModal<IBrand>>({ ..._modal });
  const [form] = Form.useForm();

  const { data, refetch } = useQuery<IBrandRes, Error>({
    queryKey: ['search-brand-list'],
    queryFn: searchBrands,
    retry: 0,
  });

  const actionBrandMutation = useMutation({
    mutationFn: async (data: Partial<IBrand>) =>
      action === 'create' ? await addBrand(data) : await updateBrand(data),
    mutationKey: ['add-brand'],
    onSuccess: data => {
      if (data.status === 1) {
        notification.success({ message: 'Sync Brand สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Sync Brand ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const matchBrandApi = useMutation({
    mutationFn: async (data: Partial<IBrand>) =>
      action === (await matchBrand(data)),
    mutationKey: ['add-brand'],
    onSuccess: data => {
      setModal({ ..._modal });
      notification.success({ message: 'Match Brand สำเร็จ' });
      refetch();
      form.resetFields();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedBrands = async () => {
    for (const brand of selectedRows) {
      await actionBrandMutation.mutateAsync(brand);
    }
  };

  const actionTOERP = useMutation({
    mutationFn: async () => await synctoERP({ MASTER_TYPE: 'BRAND' }),
    mutationKey: ['sync-to-erp'],
    onSuccess: data => {
      notification.success({ message: 'Sync Brand สำเร็จ' });
      refetch();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncTOERP = async () => {
    await actionTOERP.mutateAsync();
  };

  const handleUpdateBrandCode = useCallback((item: IBrand) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IBrand, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'Brand Code',
          dataIndex: 'BRAND_CODE',
          hidden: action === 'match',
        },
        {
          title: 'Brand Name',
          dataIndex: 'BRAND_NAME',
          hidden: action !== 'match',
        },
        {
          title: 'Brand Description',
          dataIndex: 'BRAND_DESC',
        },
        {
          title: 'BENTO ID',
          dataIndex: 'BRAND_SING_ID',
        },
        {
          title: '',
          dataIndex: 'BRAND_SING_ID',
          align: 'center',
          width: 120,
          hidden: action !== 'match',
          render: (_, rec) => (
            <button
              type="button"
              className="btn btn-success"
              onClick={() => handleUpdateBrandCode(rec)}
            >
              UPDATE
            </button>
          ),
        },
      ] as TableColumnsType<IBrand>,
    [action, handleUpdateBrandCode],
  );

  const insert = data?.data?.insert;
  const update = data?.data?.update;
  const match = data?.data?.match;

  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(
      listMap[action]?.map(c => ({ ...c, _key: uniqueId('b-') })) || [],
    );
    setSelectedRowKeys([]);
  }, [action, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IBrand[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: IBrand) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      if (action === 'match') {
        const brandName = (item.BRAND_NAME || '').toLowerCase();
        const searchValue = search.trim().toLowerCase();
        return !searchValue || brandName.includes(searchValue);
      } else {
        const brandCode = (item.BRAND_CODE || '').toUpperCase();

        // Convert search values to uppercase for case-insensitive comparison
        const fromValue = searchText1.trim().toUpperCase() || '';
        const toValue =
          searchText2.trim().toUpperCase() || 'ZZZZZZZZZZZZZZZZZZZZ';

        // If both fields are empty, show all
        if (!fromValue && toValue === 'ZZZZZZZZZZZZZZZZZZZZ') {
          return true;
        }

        // If only one field has a value, check if brand code contains that value
        if (fromValue && !searchText2.trim()) {
          return brandCode.includes(fromValue);
        }

        if (!fromValue && searchText2.trim()) {
          return brandCode.includes(toValue);
        }

        // For true BETWEEN comparison with both values
        // Check if any part of the brand code is between the values
        for (let i = 0; i < brandCode.length; i++) {
          for (let j = i + 1; j <= brandCode.length; j++) {
            const subCode = brandCode.substring(i, j);
            if (subCode >= fromValue && subCode <= toValue) {
              return true;
            }
          }
        }

        return false;
      }
    });
  }, [selected, action, search, searchText1, searchText2]);

  const onFinish: FormProps<IBrand>['onFinish'] = async values => {
    await matchBrandApi.mutateAsync({
      ...values,
      BRAND_SING_ID: modal.item?.BRAND_SING_ID,
    });
  };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex align="start" gap={12}>
          <Radio.Group
            value={action}
            onChange={e => {
              setAction(e.target.value);
              setSearchText1('');
              setSearchText2('');
            }}
            size="large"
          >
            <Radio.Button value="match">
              จับคู่ <Badge count={match?.length || 0} />
            </Radio.Button>
            <Radio.Button value="create">
              เพิ่ม <Badge count={insert?.length || 0} />
            </Radio.Button>
            <Radio.Button value="update">
              แก้ไข/ปรับปรุง <Badge count={update?.length || 0} />
            </Radio.Button>
          </Radio.Group>
          {action !== 'match' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>จาก</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchText1}
                onChange={e => {
                  setSearchText1(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.BRAND_CODE)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
              <div>ถึง</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchText2}
                onChange={e => {
                  setSearchText2(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.BRAND_CODE)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          )}
          {action === 'match' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select
                style={{ minWidth: 300 }}
                value={search}
                onChange={e => {
                  SetSearch(e || '');
                }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(
                  new Set(selected.map(item => item.BRAND_NAME)),
                )
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          )}
        </Flex>
        <Flex gap={12}>
          <button
            type="button"
            className="btn btn-info"
            style={{ minWidth: 100 }}
            onClick={() => handleSyncTOERP()}
          >
            {actionTOERP.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              '< ดึงข้อมูลมา ERP'
            )}
          </button>
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedBrands()}
          >
            {actionBrandMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : (
              'ส่งข้อมูลไป BENTO >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IBrand>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          loading={matchBrandApi.isPending}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                onFinish(values);
              })
              .catch(() => {
                // validation failed
              });
          }}
        >
          <Form
            form={form}
            name="brand"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<IBrand>
              label="BRAND CODE"
              name="BRAND_CODE"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(brand => ({
                  label: `${brand.BRAND_CODE} - ${brand.BRAND_DESC}`,
                  value: brand.BRAND_CODE,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default BrandPage;
